import React, { useState, useEffect } from 'react';
import moment from 'moment';
import {
  Body,
  Container,
  Header,
  BreadcrumbsContainer,
  Breadcrumb,
  SavingStatusText,
  PausedBannerRow,
  InfoContainerWrapper,
} from './Step.styled';
import { Close } from '@material-ui/icons';
import { EditIcon, NextIcon } from '../../SVGs/Icons';
import { useEditor } from '../CampaignEditor';
import EditCampaignDetailsDrawer from '../EditCampaignDetailsDrawer/EditCampaignDetailsDrawer';
import { useAudienceValidation } from '../AudienceEditor/AudienceEditor';
import { AutosaveStatus } from '../../hooks/useCampaignAutosave';

const MESSAGE_BY_SAVE_STATUS = {
  [AutosaveStatus.SAVING]: 'Saving...',
  [AutosaveStatus.SAVED]: 'All changes saved',
  [AutosaveStatus.UNSAVED]: 'Unsaved changes',
};

const Step = ({
  className = null,
  title = null,
  // description = null,
  headerCenter = true,
  headerRightAddon = null,
  onBack = null,
  onNext = () => {},
  nextText = null,
  isNextDisabled = false,
  isNextDisabledVisual = false,
  withoutVerticalPaddingInBody = false,
  withoutHorizontalPaddingInBody = false,
  children,
  autosaveStatus,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const editor = useEditor();
  const isPausedBannerDismissed = editor.isPausedBannerDismissed ?? false;
  const setPausedBannerDismissed = editor.setPausedBannerDismissed;
  const currentStep = editor.step ?? '';
  const originalStatus = editor.getField('originalStatus');
  const wasActiveCampaign = originalStatus && originalStatus !== 'unfinished';
  const showPausedBanner =
    wasActiveCampaign &&
    !isPausedBannerDismissed &&
    currentStep !== 'template/editor';

  // Get autosaveStatus from prop or editor context
  const status = autosaveStatus ?? editor.getAutosaveStatus?.();

  // Persist default name to editor when missing so autosave saves it to the database
  useEffect(() => {
    const currentName = editor.getField('name');
    if (currentName == null || String(currentName).trim() === '') {
      editor.setField(
        'name',
        `New Campaign ${moment().format('MM-DD-YYYY HH:mm:ss')}`
      );
    }
  }, []);

  const campaignName =
    editor.getField('name') ||
    `New Campaign ${moment().format('MM-DD-YYYY HH:mm:ss')}`;
  const campaignDescription = editor.getField('description') || '';

  // Validation logic for each step
  const isAudienceValidated = useAudienceValidation();

  const isStagesValidated = () => {
    const stages = editor.getField('stages') || [];
    return stages.length > 0;
  };

  const handleEditClick = () => {
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const handleDrawerSave = ({ name, description }) => {
    editor.setField('name', name);
    editor.setField('description', description);
  };

  const isBackDisabled = status !== AutosaveStatus.SAVED;

  const backButton =
    typeof onBack === 'string' ? (
      <Header.BackButton to={onBack} disabled={isBackDisabled} />
    ) : (
      <Header.BackButton onClick={onBack} disabled={isBackDisabled} />
    );

  return (
    <>
      <Container>
        <Header>
          <Header.Left>
            {backButton}
            <Header.TitleContainer>
              <Header.Title>{campaignName}</Header.Title>
              <Header.Description>{campaignDescription}</Header.Description>
            </Header.TitleContainer>
            <Header.EditButton onClick={handleEditClick}>
              <EditIcon /> Edit Campaign Details
            </Header.EditButton>
            {status !== undefined && (
              <SavingStatusText>
                {MESSAGE_BY_SAVE_STATUS[status] || 'All changes saved'}
              </SavingStatusText>
            )}
          </Header.Left>
          {headerCenter && (
            <Header.Center>
              <BreadcrumbsContainer>
                <Breadcrumb
                  active={title === 'Stages'}
                  validated={isStagesValidated()}
                  onClick={() => editor.goToStep('stages')}
                >
                  Stage Builder
                </Breadcrumb>
                <NextIcon />
                <Breadcrumb
                  active={title === 'Audience'}
                  validated={isAudienceValidated}
                  onClick={() => editor.goToStep('audience/editor')}
                >
                  Audience
                </Breadcrumb>
                <NextIcon />
                <Breadcrumb
                  active={title === 'Review Campaign'}
                  onClick={() => editor.goToStep('review-campaign')}
                >
                  Review Campaign
                </Breadcrumb>
              </BreadcrumbsContainer>
            </Header.Center>
          )}

          <Header.Right>
            {headerRightAddon && <div>{headerRightAddon}</div>}
            {nextText && (
              <Header.NextButton
                onClick={onNext}
                disabled={isNextDisabled}
                isDisabledVisual={isNextDisabledVisual}
              >
                {nextText}
              </Header.NextButton>
            )}
          </Header.Right>
        </Header>
        <Body
          withoutVerticalPadding={withoutVerticalPaddingInBody}
          withoutHorizontalPadding={withoutHorizontalPaddingInBody}
        >
          {showPausedBanner && (
            <PausedBannerRow>
              <InfoContainerWrapper>
                <InfoContainerWrapper.CloseButton
                  type="button"
                  onClick={() => setPausedBannerDismissed?.(true)}
                  aria-label="Dismiss"
                >
                  <Close />
                </InfoContainerWrapper.CloseButton>
                This campaign was paused for editing and flagged as
                “Unfinished.” Click “Finish” to publish all changes and resume
                the campaign.
              </InfoContainerWrapper>
            </PausedBannerRow>
          )}
          <div className={className}>{children}</div>
        </Body>
      </Container>
      <EditCampaignDetailsDrawer
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        campaignName={campaignName}
        description={campaignDescription}
        onSave={handleDrawerSave}
      />
    </>
  );
};

export default Step;
